// Cache for gradients to avoid recreating them on every render
const gradientCache = new Map<string, CanvasGradient>();

// Parse CSS linear-gradient string to extract colors and direction
export const parseLinearGradient = (gradientString: string) => {
  // Match pattern: linear-gradient(to bottom, color1, color2, ...)
  const match = gradientString.match(/linear-gradient\(([^)]+)\)/);
  if (!match) return null;

  const parts = match[1].split(',').map((part) => part.trim());
  const direction = parts[0]; // e.g., "to bottom"
  const colors = parts.slice(1); // e.g., ["#color1", "#color2", ...]

  return { direction, colors };
};

// Convert hex color to rgba with specified alpha
const hexToRgba = (hex: string, alpha: number): string => {
  // Remove # if present
  hex = hex.replace('#', '');

  // Handle 3-digit hex
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Apply alpha to a color (supports both hex and rgba)
const applyAlphaToColor = (color: string, alpha: number): string => {
  color = color.trim();

  // If it's already rgba, extract rgb and apply new alpha
  const rgbaMatch = color.match(/rgba?\(([^)]+)\)/);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(',').map((p) => p.trim());
    const r = parts[0];
    const g = parts[1];
    const b = parts[2];
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // If it's hex, convert to rgba
  if (color.startsWith('#')) {
    return hexToRgba(color, alpha);
  }

  // For named colors or other formats, try to parse as hex
  return hexToRgba(color, alpha);
};

// Create Chart.js gradient from CSS linear-gradient string for individual bars
export const createBarGradient = (
  ctx: CanvasRenderingContext2D,
  barBounds: { x: number; y: number; width: number; height: number },
  gradientString: string,
  isHorizontal = false,
  alpha = 1.0
): CanvasGradient | null => {
  // Create a unique cache key for this specific bar including alpha
  const cacheKey = `${gradientString}-${barBounds.x}-${barBounds.y}-${barBounds.width}-${barBounds.height}-${isHorizontal}-${alpha}`;

  if (gradientCache.has(cacheKey)) {
    return gradientCache.get(cacheKey)!;
  }

  const parsed = parseLinearGradient(gradientString);
  if (!parsed) return null;

  const { direction, colors } = parsed;

  // Determine gradient direction based on CSS direction and chart orientation
  let x0, y0, x1, y1;

  if (isHorizontal) {
    // For horizontal charts, gradient goes from left edge to right edge of the bar
    x0 = barBounds.x;
    y0 = barBounds.y + barBounds.height / 2; // Center vertically
    x1 = barBounds.x + barBounds.width;
    y1 = barBounds.y + barBounds.height / 2; // Center vertically
  } else {
    // For vertical charts, gradient goes from bottom to top of the bar
    x0 = barBounds.x + barBounds.width / 2; // Center horizontally
    y0 = barBounds.y + barBounds.height; // Bottom of bar
    x1 = barBounds.x + barBounds.width / 2; // Center horizontally
    y1 = barBounds.y; // Top of bar
  }

  // Handle different gradient directions
  if (direction.includes('to top') && !isHorizontal) {
    [y0, y1] = [y1, y0]; // Reverse vertical direction
  } else if (direction.includes('to left') && isHorizontal) {
    [x0, x1] = [x1, x0]; // Reverse horizontal direction
  }

  const gradient = ctx.createLinearGradient(x0, y0, x1, y1);

  // Add color stops evenly distributed with applied alpha
  colors.forEach((color, index) => {
    const stop = index / (colors.length - 1);
    const colorWithAlpha = applyAlphaToColor(color.trim(), alpha);
    gradient.addColorStop(stop, colorWithAlpha);
  });

  gradientCache.set(cacheKey, gradient);
  return gradient;
};

// Legacy function for chart-wide gradients (kept for compatibility)
export const createChartGradient = (
  ctx: CanvasRenderingContext2D,
  chartArea: any,
  gradientString: string,
  isHorizontal = false
): CanvasGradient | null => {
  // Use chart area as bar bounds for legacy support
  const barBounds = {
    x: chartArea.left,
    y: chartArea.top,
    width: chartArea.right - chartArea.left,
    height: chartArea.bottom - chartArea.top,
  };

  return createBarGradient(ctx, barBounds, gradientString, isHorizontal);
};

// Check if a color string is a gradient
export const isGradient = (color: string): boolean => {
  return color.startsWith('linear-gradient(');
};

// Create a gradient function for Chart.js datasets that creates per-bar gradients
export const createGradientFunction = (
  gradientString: string,
  isHorizontal = false,
  alpha = 1.0
) => {
  return function (context: any) {
    const chart = context.chart;
    const { ctx, chartArea } = chart;

    if (!chartArea) {
      // This case happens on initial chart load
      return gradientString; // Fallback to original string
    }

    // Get the bar element to determine its bounds
    const datasetIndex = context.datasetIndex;
    const dataIndex = context.dataIndex;

    // Try to get the bar element from the chart
    const meta = chart.getDatasetMeta(datasetIndex);
    const element = meta?.data?.[dataIndex];

    if (element && element.x !== undefined && element.y !== undefined) {
      // Calculate bar bounds based on the element's position and size
      let barBounds;

      if (isHorizontal) {
        // For horizontal bars
        const barHeight = element.height || 20; // Fallback height
        const barWidth = Math.abs(element.x - element.base);
        barBounds = {
          x: Math.min(element.x, element.base),
          y: element.y - barHeight / 2,
          width: barWidth,
          height: barHeight,
        };
      } else {
        // For vertical bars
        const barWidth = element.width || 20; // Fallback width
        const barHeight = Math.abs(element.y - element.base);
        barBounds = {
          x: element.x - barWidth / 2,
          y: Math.min(element.y, element.base),
          width: barWidth,
          height: barHeight,
        };
      }

      return createBarGradient(
        ctx,
        barBounds,
        gradientString,
        isHorizontal,
        alpha
      );
    }

    // Fallback to chart-wide gradient if we can't get bar bounds
    return createChartGradient(ctx, chartArea, gradientString, isHorizontal);
  };
};

// Clear gradient cache (useful for cleanup)
export const clearGradientCache = () => {
  gradientCache.clear();
};
